import config from 'isomorphic-config'
import analytics from '@/shared-utils/analytics'
import { GraphResponseData } from '@/types'
import { DealDataResponse } from './types'
import { jsonContentTypeHeader } from '../../server/constants'
import { hasNumberProp, hasStringProp } from '../../utils/typeguard-helpers'

type InputType = {
  latitude: number
  longitude: number
  appName: string
  appVersion: string
  shouldUseStellate?: boolean
}

type GetBundleDealsResponse = {
  getBundleDeals: {
    bundles: DealDataResponse[]
  }
}
const PKG_DEALS_QUERY = `
  query getBundleDeals($input: BundleDealsInput) {
    getBundleDeals(input: $input) {
      bundles {
        cityName
        cityImageUrl
        priceAmount
        retailPriceAmount
        searchLink
        productName
        description
      }
    }
  }
`

export function isDealDataResponseType(obj: unknown): obj is DealDataResponse {
  if (typeof obj !== 'object' || obj === null) {
    return false
  }
  return (
    hasStringProp(obj, 'cityName') &&
    hasStringProp(obj, 'cityImageUrl') &&
    hasStringProp(obj, 'productName') &&
    hasStringProp(obj, 'description') &&
    hasStringProp(obj, 'searchLink') &&
    hasNumberProp(obj, 'priceAmount') &&
    hasNumberProp(obj, 'retailPriceAmount')
  )
}

function isDealDataResponseTypeArray(
  array: unknown[]
): array is DealDataResponse[] {
  return array.every(isDealDataResponseType)
}

function isValidGraphQLResponseAndData(
  payload: unknown
): payload is GraphResponseData<GetBundleDealsResponse> {
  if (payload && payload instanceof Object) {
    if ('data' in payload && payload.data && typeof payload.data === 'object') {
      if ('getBundleDeals' in payload.data) {
        return true
      }
    }
  }
  return false
}
const OPERATION_NAME = 'getBundleDeals'

const truncateLocation = (value: number) => Number(value.toFixed(3))

export const getBundleDeals = async ({
  latitude,
  longitude,
  appName,
  appVersion,
  shouldUseStellate
}: InputType) => {
  const graphConfig = shouldUseStellate ? 'stellate-graph-cdn' : 'pcln-graph'
  const { url, timeout } = config.client[graphConfig]
  const requestUrl = `${url}?gqlOp=${OPERATION_NAME}`
  const options = {
    signal: AbortSignal.timeout(timeout),
    method: 'POST',
    headers: {
      ...jsonContentTypeHeader,
      'apollographql-client-name': appName,
      'apollographql-client-version': appVersion
    },
    body: JSON.stringify({
      query: PKG_DEALS_QUERY,
      variables: {
        input: {
          latitude: shouldUseStellate ? truncateLocation(latitude) : latitude,
          longitude: shouldUseStellate ? truncateLocation(longitude) : longitude
        }
      },
      operationName: OPERATION_NAME
    })
  }
  try {
    const response = await fetch(requestUrl, options)
    const { status, statusText, type, url: urlValue } = response
    if (!response.ok) {
      analytics.logError({
        message: `Network response for getBundleDeals was not ok for ${requestUrl}`,
        response: {
          status,
          statusText,
          type,
          url: urlValue
        }
      })
    } else {
      const responseData = await response.json()
      if (isValidGraphQLResponseAndData(responseData)) {
        const { bundles } = responseData.data.getBundleDeals
        if (isDealDataResponseTypeArray(bundles)) {
          return bundles
        }
      }
      if (responseData.data.errors) {
        const { errors } = responseData.data
        analytics.logError({
          message: 'Failed to call Bundle Deals API!',
          lat: latitude,
          lon: longitude,
          errorObject: errors
        })
        return []
      }
    }
    return []
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error'
    if ((error as Error).name === 'TimeoutError') {
      analytics.logError({
        message: `Timeout reached while invoking getBundleDeals graph query.`,
        errorObject: errorMessage
      })
    } else {
      analytics.logError({
        message: `Failed to query for getBundleDeals data.`,
        errorObject: errorMessage
      })
    }
    return []
  }
}

export default getBundleDeals
